import CheckIcon from '@mui/icons-material/CheckCircle';
import LogoutIcon from '@mui/icons-material/Logout';
import { Box, GlassAlert, GlassButton, ShimmerLoader, Typography } from '@ops-matrix/design-system-ui';
import { signOut } from 'firebase/auth';
import { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { auth } from '../config/firebase';
import { isValidRedirectUrl, useVerifiedDomains } from '../hooks/useRedirect';
import { useTheme } from '../theme/ThemeProvider';

function validateRedirectUrl(
  redirectUrl: string | null | undefined,
  currentOrigin: string,
  verifiedCustomDomains: ReadonlySet<string>,
): string | null {
  if (!redirectUrl) return null;

  try {
    const url = new URL(redirectUrl);
    if (url.origin === currentOrigin) return redirectUrl;
    if (isValidRedirectUrl(redirectUrl, verifiedCustomDomains)) return redirectUrl;

    console.warn(`[Auth] Blocked redirect to untrusted origin: ${url.origin}`);
    return null;
  } catch {
    // Allow relative paths (single /), but not protocol-relative (// → //evil.com)
    if (redirectUrl.startsWith('/') && !redirectUrl.startsWith('//')) {
      return `${currentOrigin}${redirectUrl}`;
    }
    console.warn(`[Auth] Invalid redirect URL format: ${redirectUrl}`);
    return null;
  }
}

export default function LogoutPage() {
  const [searchParams] = useSearchParams();
  const { portalType, tenantTheme } = useTheme();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [countdown, setCountdown] = useState(3);
  const rawRedirectUrl = searchParams.get('redirect') || searchParams.get('returnUrl');
  const verifiedDomains = useVerifiedDomains(rawRedirectUrl);

  const safeRedirectUrl = useMemo(() => {
    if (!rawRedirectUrl) return null;
    return validateRedirectUrl(rawRedirectUrl, window.location.origin, verifiedDomains);
  }, [rawRedirectUrl, verifiedDomains]);

  useEffect(() => {
    const performLogout = async () => {
      try {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('consumer_access_token');
        localStorage.removeItem('consumer_refresh_token');

        await signOut(auth);
      } catch (err) {
        setError('Failed to sign out. Please try again.');
        console.error('Logout error:', err);
      } finally {
        setIsLoading(false);
      }
    };

    performLogout();
  }, []);

  useEffect(() => {
    if (!isLoading && !error && safeRedirectUrl) {
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            window.location.href = safeRedirectUrl;
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
    return undefined;
  }, [isLoading, error, safeRedirectUrl]);

  const currentParams = searchParams.toString();
  const queryString = currentParams ? `?${currentParams}` : '';

  if (isLoading) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <LogoutIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <ShimmerLoader variant="circular" width={40} height={40} />
        </Box>
        <Typography variant="body1" color="textSecondary">
          Signing out...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ textAlign: 'center' }}>
        <GlassAlert severity="error" visible={true} sx={{ mb: 3 }}>
          {error}
        </GlassAlert>
        <GlassButton variant="gradient" onClick={() => window.location.reload()}>
          Try Again
        </GlassButton>
      </Box>
    );
  }

  return (
    <Box sx={{ textAlign: 'center' }}>
      <CheckIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
      <Typography variant="h6" sx={{ fontWeight: 600 }} gutterBottom>
        You&apos;ve been signed out
      </Typography>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
        Thank you for using {tenantTheme?.companyName ?? 'our platform'}.
      </Typography>

      {safeRedirectUrl && countdown > 0 && (
        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          Redirecting in {countdown} seconds...
        </Typography>
      )}

      <RouterLink to={`/login${queryString}`} style={{ textDecoration: 'none' }}>
        <GlassButton variant="gradient" size="large" sx={{ mb: 2 }}>
          Sign In Again
        </GlassButton>
      </RouterLink>

      {safeRedirectUrl && (
        <GlassButton
          variant="ghost"
          onClick={() => {
            window.location.href = safeRedirectUrl;
          }}
          sx={{ display: 'block', mx: 'auto' }}
        >
          Return to {portalType ? `${portalType} portal` : 'application'}
        </GlassButton>
      )}

      {!safeRedirectUrl && portalType === 'consumer' && (
        <Typography variant="body2" color="textSecondary">
          You can close this window if you&apos;re done.
        </Typography>
      )}
    </Box>
  );
}
